import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusPill } from '@/components/common/StatusPill';

function getRelativeDateLabel(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffTime = target.getTime() - now.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return { label: 'Today', tone: 'success' };
  if (diffDays === 1) return { label: 'Tomorrow', tone: 'info' };
  if (diffDays > 1 && diffDays <= 7) return { label: `In ${diffDays} days`, tone: 'warning' };
  if (diffDays > 7) return { label: `In ${diffDays} days`, tone: 'neutral' };
  if (diffDays < 0) return { label: 'Overdue', tone: 'danger' };
  return null;
}

function getTypeBadge(type = '') {
  const norm = String(type).toLowerCase();
  if (norm.includes('ticket')) {
    return {
      label: 'Ticket',
      icon: '🎫',
      style: { background: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' }
    };
  }
  return {
    label: 'FMS',
    icon: '📋',
    style: { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }
  };
}

export function DashboardUpcomingSection({
  tasks = [],
  showOwner = false,
  loading = false
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Filter out To-Do items: Dashboard upcoming tracks operational Tickets and FMS only
  const operationalTasks = useMemo(() => {
    return (tasks || []).filter((task) => {
      const type = String(task.Type || '').toLowerCase();
      return !type.includes('todo') && !type.includes('to-do');
    });
  }, [tasks]);

  // Counts by type
  const counts = useMemo(() => {
    let tickets = 0;
    let fms = 0;
    operationalTasks.forEach((t) => {
      const type = String(t.Type || '').toLowerCase();
      if (type.includes('ticket')) tickets += 1;
      else fms += 1;
    });
    return { all: operationalTasks.length, tickets, fms };
  }, [operationalTasks]);

  // Filter tasks based on activeTab and search
  const filteredTasks = useMemo(() => {
    return operationalTasks.filter((task) => {
      const type = String(task.Type || '').toLowerCase();
      if (activeTab === 'tickets' && !type.includes('ticket')) return false;
      if (activeTab === 'fms' && type.includes('ticket')) return false;

      if (search.trim()) {
        const query = search.toLowerCase();
        const id = String(task.ID || '').toLowerCase();
        const desc = String(task.Description || '').toLowerCase();
        const user = String(task.User || '').toLowerCase();
        const status = String(task.Status || '').toLowerCase();
        if (!id.includes(query) && !desc.includes(query) && !user.includes(query) && !status.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [operationalTasks, activeTab, search]);

  const totalFiltered = filteredTasks.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, pageCount);
  const pagedTasks = filteredTasks.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleActionClick = (task) => {
    const type = String(task.Type || '').toLowerCase();
    if (type.includes('ticket')) {
      navigate('/ticket-system');
    } else {
      navigate('/fms-tracker');
    }
  };

  return (
    <article className="migration-panel migration-panel--full" style={{ marginBottom: '24px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          paddingBottom: '14px',
          borderBottom: '1px solid #f1f5f9'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
            }}
          >
            ⏱️
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Upcoming Tasks
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
              Scheduled tickets & FMS tasks for today and future dates
            </p>
          </div>
        </div>

        {/* Tab Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`attendance-cta ${activeTab === 'all' ? 'attendance-cta--blue' : 'attendance-cta--gray'}`}
            style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '20px' }}
            onClick={() => { setActiveTab('all'); setPage(1); }}
          >
            All ({counts.all})
          </button>
          <button
            type="button"
            className={`attendance-cta ${activeTab === 'tickets' ? 'attendance-cta--blue' : 'attendance-cta--gray'}`}
            style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '20px' }}
            onClick={() => { setActiveTab('tickets'); setPage(1); }}
          >
            🎫 Tickets ({counts.tickets})
          </button>
          <button
            type="button"
            className={`attendance-cta ${activeTab === 'fms' ? 'attendance-cta--blue' : 'attendance-cta--gray'}`}
            style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '20px' }}
            onClick={() => { setActiveTab('fms'); setPage(1); }}
          >
            📋 FMS ({counts.fms})
          </button>
        </div>
      </div>

      {/* Filter / Search input */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', marginBottom: '12px', gap: '10px' }}>
        <div style={{ maxWidth: '320px', width: '100%' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search upcoming tasks..."
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.88rem',
              outline: 'none',
              background: '#f8fafc'
            }}
          />
        </div>
        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
          Showing <strong>{filteredTasks.length}</strong> upcoming task{filteredTasks.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Upcoming Table */}
      <div className="dashboard-table-wrap">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th style={{ width: '150px' }}>Plan Date</th>
              <th style={{ width: '160px' }}>ID</th>
              <th style={{ width: '110px' }}>Type</th>
              {showOwner ? <th style={{ width: '130px' }}>User</th> : null}
              <th>Description</th>
              <th style={{ width: '90px' }}>TAT</th>
              <th style={{ width: '110px' }}>Status</th>
              <th style={{ width: '90px', textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showOwner ? 8 : 7} className="dashboard-table__empty">
                  Loading upcoming tasks...
                </td>
              </tr>
            ) : pagedTasks.length ? (
              pagedTasks.map((task, index) => {
                const badge = getTypeBadge(task.Type);
                const relDate = getRelativeDateLabel(task.Date);
                return (
                  <tr key={`${task.ID || 'up-task'}-${index}`}>
                    <td data-label="Plan Date">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{task.Date || '-'}</span>
                        {relDate ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 'fit-content',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: '12px',
                              background: relDate.tone === 'success' ? '#dcfce7' : relDate.tone === 'info' ? '#dbeafe' : '#fef3c7',
                              color: relDate.tone === 'success' ? '#15803d' : relDate.tone === 'info' ? '#1d4ed8' : '#b45309'
                            }}
                          >
                            {relDate.label}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="ID" style={{ fontFamily: 'monospace', fontWeight: 600, color: '#334155' }}>
                      {task.ID || '-'}
                    </td>
                    <td data-label="Type">
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: '6px',
                          ...badge.style
                        }}
                      >
                        <span>{badge.icon}</span>
                        <span>{badge.label}</span>
                      </span>
                    </td>
                    {showOwner ? (
                      <td data-label="User" style={{ fontWeight: 500, color: '#1e293b' }}>
                        {task.User || '-'}
                      </td>
                    ) : null}
                    <td data-label="Description" style={{ color: '#334155', maxWidth: '340px' }}>
                      <span title={task.Description} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {task.Description || '-'}
                      </span>
                    </td>
                    <td data-label="TAT" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      {task.TAT ? `${task.TAT}m` : '-'}
                    </td>
                    <td data-label="Status">
                      <StatusPill tone="info">{task.Status || 'Open'}</StatusPill>
                    </td>
                    <td data-label="Action" style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleActionClick(task)}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          color: '#1e293b',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#e2e8f0';
                          e.currentTarget.style.borderColor = '#94a3b8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                          e.currentTarget.style.borderColor = '#cbd5e1';
                        }}
                      >
                        View ↗
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={showOwner ? 8 : 7} style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>🎉</div>
                  <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.98rem' }}>
                    No upcoming tasks found
                  </div>
                  <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>
                    {search ? 'Try clearing your search query.' : 'All scheduled tickets, FMS tasks, and to-dos are complete!'}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 ? (
        <div className="react-data-table__footer" style={{ marginTop: '14px' }}>
          <span className="react-data-table__info">
            Page {safePage} of {pageCount} ({totalFiltered} total items)
          </span>
          <div className="react-data-table__pager">
            <button
              type="button"
              className="react-data-table__pager-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || safePage <= 1}
            >
              Prev
            </button>
            <span className="react-data-table__pager-current">{safePage}</span>
            <button
              type="button"
              className="react-data-table__pager-btn"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={loading || safePage >= pageCount}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
